import { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from 'framer-motion';
import { useIsMobile } from '../hooks/useIsMobile';

export const AcidTabAnimation = () => {
  const containerRef = useRef(null);
  const [isComplete, setIsComplete] = useState(false);
  const isMobile = useIsMobile();

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const tabX = useTransform(smoothProgress, [0, 0.3, 0.5], [-100, 0, 0]);
  const tabY = useTransform(smoothProgress, [0, 0.3, 0.5], [-50, 0, 30]);
  const tabScale = useTransform(smoothProgress, [0, 0.3, 0.5, 0.7], [1.5, 1, 0.8, 0]);
  const tabRotate = useTransform(smoothProgress, [0, 0.3, 0.5], [45, 0, -10]);
  const tabOpacity = useTransform(smoothProgress, [0, 0.2, 0.5, 0.7], [0, 1, 1, 0]);

  const tongueScale = useTransform(smoothProgress, [0.3, 0.5, 0.7], [0.8, 1, 1.1]);
  const tongueY = useTransform(smoothProgress, [0.3, 0.5, 0.7], [20, 0, -10]);

  const cloudOpacity = useTransform(smoothProgress, [0.5, 0.7, 1], [0, 0.5, 1]);
  const cloudScale = useTransform(smoothProgress, [0.5, 0.7, 1], [0.5, 0.8, 1]);

  useEffect(() => {
    const unsubscribe = scrollYProgress.on("change", (latest) => {
      setIsComplete(latest > 0.6);
    });
    return () => unsubscribe();
  }, [scrollYProgress]);

  const particleCount = isMobile ? 3 : 20;

  return (
    <div
      ref={containerRef}
      className="relative min-h-[120vh] md:min-h-[200vh] flex items-center justify-center"
      data-testid="acid-animation-container"
    >
      <div className="sticky top-0 h-screen w-full flex items-center justify-center overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A0510] via-[#2D0A1E] to-[#1A0510]" />

        {/* Floating particles — reduced on mobile */}
        <div className="absolute inset-0">
          {[...Array(particleCount)].map((_, i) => (
            <motion.div
              key={`acid-p-${i}`}
              className="absolute w-1 h-1 rounded-full bg-pink-400"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -25, 0],
                opacity: [0.2, 0.7, 0.2],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        {/* Main container */}
        <div className="relative z-10 flex flex-col items-center">
          {/* Tongue */}
          <motion.div
            className="relative"
            style={{ scale: tongueScale, y: tongueY }}
          >
            <div className="tongue-container">
              <div className="w-20 h-28 md:w-24 md:h-32 relative">
                <svg viewBox="0 0 100 140" className="w-full h-full">
                  <defs>
                    <linearGradient id="tongueGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#FF6B9D" />
                      <stop offset="50%" stopColor="#FF1493" />
                      <stop offset="100%" stopColor="#CC0066" />
                    </linearGradient>
                  </defs>
                  <ellipse
                    cx="50" cy="70" rx="40" ry="60"
                    fill="url(#tongueGradient)"
                  />
                  <line
                    x1="50" y1="40" x2="50" y2="100"
                    stroke="rgba(0,0,0,0.15)"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </div>
          </motion.div>

          {/* Acid Tab */}
          <motion.div
            className="absolute"
            style={{
              x: tabX, y: tabY, scale: tabScale,
              rotate: tabRotate, opacity: tabOpacity,
            }}
          >
            <div
              className="w-10 h-10 md:w-12 md:h-12 rounded-sm flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #CCFF00 0%, #FF00FF 100%)',
                ...(isMobile ? {} : { boxShadow: '0 0 30px rgba(204, 255, 0, 0.6)' }),
              }}
            >
              <span className="font-handwritten text-xs text-[#1A0510] font-bold">
                ✦
              </span>
            </div>
          </motion.div>

          {/* Title text */}
          <motion.div
            className="absolute -top-32 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-heading text-2xl md:text-4xl text-white/80 tracking-wider">
              EXPERIENCE THE
            </h2>
            <h3 className="font-heading text-4xl md:text-6xl text-gradient-pink mt-2">
              AWAKENING
            </h3>
          </motion.div>

          {/* Pink Cloud Formation */}
          <AnimatePresence>
            {isComplete && (
              <motion.div
                className="absolute -bottom-40 md:-bottom-60"
                initial={{ opacity: 0, scale: 0.3 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.3 }}
                transition={{ duration: 1, ease: "easeOut" }}
              >
                <motion.div style={{ opacity: cloudOpacity, scale: cloudScale }}>
                  <svg
                    viewBox="0 0 400 300"
                    className="w-64 h-48 md:w-96 md:h-72"
                  >
                    <defs>
                      <radialGradient id="cloudGradient" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#FFD1DC" stopOpacity="0.9" />
                        <stop offset="40%" stopColor="#FF69B4" stopOpacity="0.7" />
                        <stop offset="70%" stopColor="#FF1493" stopOpacity="0.5" />
                        <stop offset="100%" stopColor="#FF00FF" stopOpacity="0.3" />
                      </radialGradient>
                    </defs>

                    {/* Outer cloud — simple opacity pulse instead of rx/ry animation on mobile */}
                    {isMobile ? (
                      <motion.ellipse
                        cx="200" cy="150" rx="150" ry="100"
                        fill="url(#cloudGradient)"
                        animate={{ opacity: [0.7, 1, 0.7] }}
                        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                      />
                    ) : (
                      <motion.ellipse
                        cx="200" cy="150" rx="150" ry="100"
                        fill="url(#cloudGradient)"
                        animate={{ rx: [150, 160, 150], ry: [100, 110, 100] }}
                        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                      />
                    )}

                    <ellipse cx="200" cy="150" rx="100" ry="60" fill="#FF69B4" opacity="0.6" />
                    <ellipse cx="200" cy="150" rx="40" ry="25" fill="#FFD1DC" opacity="0.8" />
                    <ellipse cx="200" cy="140" rx="15" ry="8" fill="white" opacity="0.4" />
                  </svg>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Side decorations — skip on mobile */}
        {!isMobile && (
          <>
            <motion.div
              className="absolute left-10 top-1/3"
              animate={{ y: [0, -20, 0], rotate: [0, 10, 0] }}
              transition={{ duration: 5, repeat: Infinity }}
            >
              <div className="w-4 h-4 bg-[#CCFF00] rounded-full opacity-60"
                style={{ boxShadow: '0 0 20px #CCFF00' }}
              />
            </motion.div>
            <motion.div
              className="absolute right-10 top-1/2"
              animate={{ y: [0, 20, 0], rotate: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              <div className="w-3 h-3 bg-[#00FFFF] rounded-full opacity-60"
                style={{ boxShadow: '0 0 15px #00FFFF' }}
              />
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
};

export default AcidTabAnimation;
